// Global Instructions Rule Applied!
//
// Configuration → Agent Gateway services: the aggregate admin read, the usage
// read, and the writes over cfg.agents (per-product volume caps) and
// cfg.agent_tools (per-tool value_caps).
//
// Same fetch conventions as documentConfigService. Every route is admin-gated
// at the backend AND inside the RPC.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AgentConfigValueKey,
  AgentGatewayConfigAdminData,
  AgentGatewayUsageData,
  AgentProductAdminRow,
  AgentToolAdminRow,
  validateAgentProductAdminRow,
  validateAgentToolAdminRow
} from '../../../state_manager/data_models/agent_gateway_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// Writes use the house request envelope { data: {...} }; error bodies carry
// `message` first (#232).
async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchAgentGatewayConfig = async (
  accessToken: string | null
): Promise<AgentGatewayConfigAdminData> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_GATEWAY_CONFIG),
    { method: API_ROUTES.CONFIG.AGENT_GATEWAY_CONFIG.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const data = body.data || {}
  const products = (data.products || []) as AgentProductAdminRow[]
  const tools = (data.tools || []) as AgentToolAdminRow[]
  validateAgentProductAdminRow(products, { isArray: true })
  validateAgentToolAdminRow(tools, { isArray: true })
  return {
    resolvedEnvironment: data.resolved_environment || 'global',
    products,
    tools,
    capKeys: (data.cap_keys || []) as AgentConfigValueKey[]
  }
}

export const fetchAgentGatewayUsage = async (
  accessToken: string | null,
  days = 1
): Promise<AgentGatewayUsageData> => {
  const url = `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_GATEWAY_USAGE)}?days=${days}`
  const response = await fetch(url, {
    method: API_ROUTES.CONFIG.AGENT_GATEWAY_USAGE.method,
    headers: authHeaders(accessToken)
  })
  const body = await readJson(response)
  const data = body.data || {}
  return {
    since_utc: data.since_utc,
    days: data.days,
    by_agent: data.by_agent || [],
    by_grant: data.by_grant || [],
    recent_minutes: data.recent_minutes || []
  }
}

export interface AgentProductFields {
  enabled?: boolean
  /** 0 = uncapped. A falsy value is meaningful here, so it must be sent. */
  requests_per_minute?: number
  daily_row_limit?: number
}

export const updateAgentProduct = async (
  accessToken: string | null,
  agentKey: string,
  fields: AgentProductFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_GATEWAY_PRODUCT_UPDATE, { agentKey }),
    {
      method: API_ROUTES.CONFIG.AGENT_GATEWAY_PRODUCT_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export interface AgentToolFields {
  enabled?: boolean
  /** REPLACES the stored bag with the RPC-validated subset, which is what lets
   *  a cap be removed. Omit to leave it untouched. */
  value_caps?: Record<string, unknown>
}

export const updateAgentTool = async (
  accessToken: string | null,
  toolKey: string,
  fields: AgentToolFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.AGENT_GATEWAY_TOOL_UPDATE, { toolKey }),
    {
      method: API_ROUTES.CONFIG.AGENT_GATEWAY_TOOL_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}
